/**
 * Normalises a blocked action into MessageBar / Toast state.
 *
 * A delete or operation can be refused in four shapes, and the UI should not care
 * which one it got:
 *
 *   1. client-side guard   { valid: false, error: 'key', title: 'key' }
 *   2. server route body   { error: 'key', title: 'key' }
 *   3. preventDelete body  { error: { name: 'titleKey', message: ['key', ...] } }
 *   4. batch response      { results: [], errors: [{ id, error, title }] }
 *
 * Returns `null` when the source is not a rejection, so callers can write
 * `const msg = toGuardMessage(response); if (msg) setMessage(msg)`.
 *
 * Values are returned as translation KEYS — MessageBar and Toast translate them.
 */
const FALLBACK_TITLE = 'error.validation.title'
const FALLBACK_MESSAGE = 'error.validation.generic'

export const toGuardMessage = (source, type = 'warning') => {
    if (!source || typeof source !== 'object') return null

    // 1. client-side runValidationLogic result
    if (source.valid === false) {
        return {
            show: true,
            type,
            title: source.title || FALLBACK_TITLE,
            message: source.error || FALLBACK_MESSAGE
        }
    }

    // 4. batch responses carry per-item failures; report the first one
    const batchError = Array.isArray(source.errors) && source.errors.length ? source.errors[0] : null
    const raw = batchError?.error ?? source.error

    if (!raw) {
        // Some endpoints only signal failure via status
        if (source.status === 'error') {
            return { show: true, type, title: FALLBACK_TITLE, message: FALLBACK_MESSAGE }
        }
        return null
    }

    // 3. preventDelete returns { name, message: [keys] }
    if (typeof raw === 'object') {
        return {
            show: true,
            type,
            title: raw.name || batchError?.title || source.title || FALLBACK_TITLE,
            message: raw.message || FALLBACK_MESSAGE
        }
    }

    // 2. plain { error, title }
    return {
        show: true,
        type,
        title: batchError?.title || source.title || FALLBACK_TITLE,
        message: raw
    }
}

export default toGuardMessage
